import type { TrafficTopDimension, TrafficTopRow } from '@bartizo/shared';
import { formatPercent } from '../lib/format.js';
import { trafficRisk } from '../lib/traffic-risk.js';
import { ShareBar, type Column } from '../ui/DataTable.js';
import { RiskBadge } from './StatusBadge.js';

/**
 * The insight columns every top list shares (the Traffic page's cards, the
 * explorer, a server's overview): what share of a row's answers were refusals
 * and server errors, and the risk those ratios add up to. All of it from the
 * row's own counts: rule 19 leaves nothing finer to join against.
 */

const share = (part: number, whole: number): string =>
  whole === 0 ? '—' : formatPercent((part / whole) * 100, 0);

export function refusedColumn(wideOnly = false): Column<TrafficTopRow> {
  return {
    key: 'refused',
    header: '% refused',
    align: 'end',
    wideOnly,
    sortValue: (row) => (row.requests === 0 ? 0 : row.status4xx / row.requests),
    cell: (row) =>
      row.status4xx === 0 ? (
        <span className="muted">0%</span>
      ) : (
        <span title={`${row.status4xx.toLocaleString('en-US')} answered 4xx`}>
          {share(row.status4xx, row.requests)}
        </span>
      ),
  };
}

export function errorsColumn(wideOnly = false): Column<TrafficTopRow> {
  return {
    key: 'errors',
    header: '% 5xx',
    align: 'end',
    wideOnly,
    sortValue: (row) => (row.requests === 0 ? 0 : row.status5xx / row.requests),
    cell: (row) =>
      row.status5xx === 0 ? (
        <span className="muted">0%</span>
      ) : (
        <span title={`${row.status5xx.toLocaleString('en-US')} answered 5xx`}>
          {share(row.status5xx, row.requests)}
        </span>
      ),
  };
}

const RISK_RANK = { none: 0, low: 1, medium: 2, high: 3, critical: 4 } as const;

/**
 * Only where a row is a visitor or a target. A status code's row is its own
 * answer (every 404 row is 100% refused), and a visitor type's is a mix.
 */
export const hasRisk = (dimension: TrafficTopDimension): boolean =>
  ['path', 'ip', 'bot', 'ua_family', 'host', 'site_path', 'site_ip', 'site_bot'].includes(
    dimension,
  );

export function riskColumn(): Column<TrafficTopRow> {
  return {
    key: 'risk',
    header: 'Risk',
    sortValue: (row) => RISK_RANK[trafficRisk(row).risk],
    cell: (row) => {
      const reading = trafficRisk(row);
      return <RiskBadge risk={reading.risk} why={reading.why} />;
    },
  };
}

/**
 * Requests with what became of them underneath: "33% refused · 1% failed". For a
 * list in a half-width card, where separate percentage columns would push the
 * risk off the edge; the explorer has the room for the columns.
 */
export function requestsColumn(total: number): Column<TrafficTopRow> {
  return {
    key: 'requests',
    header: 'Requests',
    align: 'end',
    sortValue: (row) => row.requests,
    cell: (row) => (
      <>
        <ShareBar value={row.requests} total={total} />
        <span className="cell-sub">
          {row.status4xx === 0 && row.status5xx === 0
            ? 'all answered normally'
            : [
                row.status4xx > 0 ? `${share(row.status4xx, row.requests)} refused` : null,
                row.status5xx > 0 ? `${share(row.status5xx, row.requests)} failed` : null,
              ]
                .filter((part) => part !== null)
                .join(' · ')}
        </span>
      </>
    ),
  };
}
